import { create } from 'zustand';
import { CheckersGame } from './checkersLogic';
import { executeAIMove } from './checkersAI';

const initialSettings = JSON.parse(localStorage.getItem('checkers_settings') || '{}');

// Helper to save game result to backend
const saveGameResult = async (result, playerColor, difficulty, variant, moveCount, startTime) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/record-game`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        result,
        opponent_type: 'ai',
        difficulty,
        player_color: playerColor,
        total_moves: moveCount,
        game_duration_seconds: startTime ? Math.floor((Date.now() - startTime) / 1000) : 0,
        game_type: 'checkers',
        variant: variant.toLowerCase()
      })
    });
    if (response.ok) {
      console.log('Checkers game result saved');
    }
  } catch (error) {
    console.log('Failed to save game result (user may not be logged in)');
  }
};

export const useCheckersStore = create((set, get) => ({
  // Game state
  game: null,
  board: [],
  currentPlayer: 'red',
  gameStatus: 'idle', // idle, playing, gameOver
  winner: null,
  moveHistory: [],
  gameStartTime: null,
  
  // Selection
  selectedPiece: null,
  validMoves: [],
  captureSequence: false,
  
  // Settings
  playerColor: initialSettings.playerColor || 'red',
  aiDifficulty: initialSettings.aiDifficulty || 'medium',
  variant: initialSettings.variant || 'AMERICAN',
  soundEnabled: initialSettings.soundEnabled ?? true,
  
  // AI state
  isAIThinking: false,
  
  // Initialize new game
  newGame: (options = {}) => {
    const playerColor = options.playerColor || get().playerColor;
    const aiDifficulty = options.aiDifficulty || get().aiDifficulty;
    const variant = options.variant || get().variant;
    
    const game = new CheckersGame(variant);
    
    set({
      game,
      board: game.board,
      currentPlayer: game.currentPlayer,
      gameStatus: 'playing',
      winner: null,
      moveHistory: [],
      selectedPiece: null,
      validMoves: [],
      captureSequence: false,
      playerColor,
      aiDifficulty,
      variant,
      isAIThinking: false,
      gameStartTime: Date.now()
    });
    
    // Save settings
    localStorage.setItem('checkers_settings', JSON.stringify({
      playerColor,
      aiDifficulty,
      variant,
      soundEnabled: get().soundEnabled
    }));
    
    // If AI moves first
    if (playerColor === 'black') {
      setTimeout(() => get().makeAIMove(), 500);
    }
  },
  
  // Select a piece
  selectPiece: (row, col) => {
    const { game, currentPlayer, playerColor, captureSequence, selectedPiece } = get();
    if (!game || currentPlayer !== playerColor) return;
    
    // If in capture sequence, can only select the capturing piece
    if (captureSequence && selectedPiece) {
      if (row === selectedPiece.row && col === selectedPiece.col) {
        // Already selected
        return;
      }
      // Check if clicking on a valid capture target
      const moves = get().validMoves;
      const targetMove = moves.find(m => m.row === row && m.col === col);
      if (targetMove) {
        get().makeMove(row, col);
        return;
      }
      return; // Can't select different piece during capture sequence
    }
    
    const piece = game.getPiece(row, col);
    if (!piece || piece.color !== playerColor) {
      set({ selectedPiece: null, validMoves: [] });
      return;
    }
    
    const validMoves = game.getValidMoves(row, col);
    
    // Check if there are forced captures
    const allMoves = game.getAllValidMoves();
    const hasCaptures = allMoves.some(m => m.to.isCapture);
    
    if (hasCaptures && !validMoves.some(m => m.isCapture)) {
      // This piece has no captures but captures are available elsewhere
      set({ selectedPiece: null, validMoves: [] });
      return;
    }
    
    set({
      selectedPiece: { row, col },
      validMoves
    });
  },
  
  // Make a move
  makeMove: (toRow, toCol) => {
    const { game, selectedPiece, validMoves, soundEnabled } = get();
    if (!game || !selectedPiece) return false;
    
    const move = validMoves.find(m => m.row === toRow && m.col === toCol);
    if (!move) return false;
    
    const result = game.makeMove(selectedPiece.row, selectedPiece.col, toRow, toCol);
    
    if (result) {
      // Play sound
      if (soundEnabled) {
        const audio = new Audio(move.isCapture ? '/capture.mp3' : '/move.mp3');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      }
      
      if (result.continueCapture) {
        // Multi-capture continues
        const newValidMoves = game.getValidMoves(toRow, toCol).filter(m => m.isCapture);
        set({
          board: [...game.board],
          selectedPiece: { row: toRow, col: toCol },
          validMoves: newValidMoves,
          captureSequence: true,
          moveHistory: [...game.moveHistory]
        });
      } else {
        // Turn complete
        const isGameOver = game.gameOver;
        const gameWinner = game.winner;
        
        set({
          board: [...game.board],
          currentPlayer: game.currentPlayer,
          selectedPiece: null,
          validMoves: [],
          captureSequence: false,
          moveHistory: [...game.moveHistory],
          gameStatus: isGameOver ? 'gameOver' : 'playing',
          winner: gameWinner
        });
        
        // Save result if game is over
        if (isGameOver) {
          const { playerColor, aiDifficulty, variant, gameStartTime, moveHistory } = get();
          const playerWon = gameWinner === playerColor;
          saveGameResult(
            playerWon ? 'win' : 'loss',
            playerColor,
            aiDifficulty,
            variant,
            moveHistory.length,
            gameStartTime
          );
        }
        
        // AI's turn
        if (!game.gameOver && game.currentPlayer !== get().playerColor) {
          setTimeout(() => get().makeAIMove(), 500);
        }
      }
      
      return true;
    }
    
    return false;
  },
  
  // AI move
  makeAIMove: () => {
    const { game, gameStatus, soundEnabled } = get();
    if (!game || gameStatus !== 'playing') return;
    
    set({ isAIThinking: true });
    
    setTimeout(() => {
      const { aiDifficulty } = get();
      const results = executeAIMove(game, aiDifficulty);
      
      if (results && results.length > 0) {
        // Play sound
        if (soundEnabled) {
          const hasCapture = game.moveHistory[game.moveHistory.length - 1]?.captured;
          const audio = new Audio(hasCapture ? '/capture.mp3' : '/move.mp3');
          audio.volume = 0.3;
          audio.play().catch(() => {});
        }
        
        const isGameOver = game.gameOver;
        const gameWinner = game.winner;
        
        set({
          board: [...game.board],
          currentPlayer: game.currentPlayer,
          selectedPiece: null,
          validMoves: [],
          captureSequence: false,
          moveHistory: [...game.moveHistory],
          gameStatus: isGameOver ? 'gameOver' : 'playing',
          winner: gameWinner,
          isAIThinking: false
        });
        
        // Save result if game is over (AI won)
        if (isGameOver) {
          const { playerColor, aiDifficulty, variant, gameStartTime, moveHistory } = get();
          const playerWon = gameWinner === playerColor;
          saveGameResult(
            playerWon ? 'win' : 'loss',
            playerColor,
            aiDifficulty,
            variant,
            moveHistory.length,
            gameStartTime
          );
        }
      } else {
        set({ isAIThinking: false });
      }
    }, 300);
  },
  
  // Settings
  setSoundEnabled: (enabled) => {
    set({ soundEnabled: enabled });
    const settings = JSON.parse(localStorage.getItem('checkers_settings') || '{}');
    settings.soundEnabled = enabled;
    localStorage.setItem('checkers_settings', JSON.stringify(settings));
  },
  
  // Reset
  resetGame: () => {
    set({
      game: null,
      board: [],
      currentPlayer: 'red',
      gameStatus: 'idle',
      winner: null,
      moveHistory: [],
      selectedPiece: null,
      validMoves: [],
      captureSequence: false,
      isAIThinking: false
    });
  }
}));
